import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Button, Divider, Icon, Input, Modal, Form as SemanticForm } from "semantic-ui-react";

import { usePermanentRealTimeUser } from "hooks";
import "modules/AccountSettings/components/ChangePasswordModal/ChangePasswordModal.scss";
import { PasswordCheckMarkIcon, PasswordField } from "modules/AccountSettings/components/ChangePasswordModal/components";

import type { ChangePasswordPayload, PasswordStrengthPayload } from "modules/AccountSettings/components/ChangePasswordModal/hooks";

type ChangePasswordNewFormProps = {
  hasErrors: boolean;
  strength: number | null;
  errorMsg: string;
  isChangePasswordPending: boolean;
  onResetErrorMsg: () => void;
  onClose: () => void;
  onSubmit: (values: ChangePasswordPayload) => Promise<void>;
  checkPasswordStrength: (payload: PasswordStrengthPayload) => void;
  onResetPasswordStrength: () => void;
};

export const ChangePasswordForm = ({
  errorMsg,
  isChangePasswordPending,
  hasErrors,
  strength,
  checkPasswordStrength,
  onSubmit,
  onClose,
  onResetErrorMsg,
  onResetPasswordStrength
}: ChangePasswordNewFormProps) => {
  const { t } = useTranslation();
  const { setValue, watch, handleSubmit } = useFormContext<ChangePasswordPayload>();

  const { user } = usePermanentRealTimeUser();
  const [newPassword, newPasswordConfirm, oldPassword] = watch(["new_password", "new_password_confirm", "old_password"]);

  const handleNewPasswordChange = (password: string) => {
    setValue("new_password", password);
    onResetErrorMsg();

    if (password.length > 0)
      checkPasswordStrength({ password: password, email: user?.email, username: user?.username, first_name: user?.first_name, last_name: user?.last_name });
    else onResetPasswordStrength();
  };

  const handleOldPasswordChange = (oldPassword: string) => {
    setValue("old_password", oldPassword);
    onResetErrorMsg();
  };

  const handleConfirmNewPasswordChange = (confirmNewPassword: string) => {
    setValue("new_password_confirm", confirmNewPassword);
    onResetErrorMsg();
  };

  return (
    <>
      <Modal.Header className="padding-container">
        <h3>{t("v8_change_password", "Change Password")}</h3>
      </Modal.Header>
      <Divider fitted />

      <Modal.Content scrolling className="padding-container">
        <p className="ChangePasswordModal-description">
          {
            (t("v8_change_password_description"),
            "Your new password must be secure. A secure password cannot contain your first name, last name, email, 'claire' or common words like 'password'. You don't need to use any special characters. A secure password contains two or more uncommon words.")
          }
        </p>
        <SemanticForm.Group widths="equal">
          <SemanticForm.Field className="password" required error={hasErrors && !oldPassword}>
            <label>{t("v8_old_password", "Old password")}</label>
            <PasswordField name="old_password" placeholder={t("v8_old_password", "Old password")} onChange={handleOldPasswordChange} />
          </SemanticForm.Field>
        </SemanticForm.Group>
        {strength !== null && (
          <div className="strength-meter">
            <div className="strength-meter-fill" data-strength={strength}></div>
          </div>
        )}

        <SemanticForm.Group widths="equal" className="password-group">
          <SemanticForm.Field className="password" required error={hasErrors && !newPassword}>
            <label>{t("v8_new_password", "New password")}</label>
            <PasswordField name="new_password" placeholder={t("v8_new_password", "New password")} onChange={handleNewPasswordChange} />
          </SemanticForm.Field>
          <PasswordCheckMarkIcon newPassword={newPassword} newPasswordConfirm={newPasswordConfirm} strength={strength} type="new_password" />
        </SemanticForm.Group>

        <SemanticForm.Group widths="equal" className="password-group">
          <SemanticForm.Field className="password" required error={hasErrors && !newPasswordConfirm}>
            <label>{t("v8_confirm_new_password", "Confirm new password")}</label>
            <PasswordField name="new_password_confirm" placeholder={t("v8_confirm_new_password", "Confirm new password")} onChange={handleConfirmNewPasswordChange} />
          </SemanticForm.Field>
          <PasswordCheckMarkIcon newPassword={newPassword} newPasswordConfirm={newPasswordConfirm} strength={strength} type="confirm_password" />
        </SemanticForm.Group>

        {hasErrors && errorMsg && (
          <div className="mt-15">
            <SemanticForm.Field error>
              <Input value={errorMsg} readOnly />
            </SemanticForm.Field>
          </div>
        )}
      </Modal.Content>
      <Divider fitted />

      <Modal.Actions className="ChangePasswordModal-action padding-container">
        <div className="inner">
          <Button size="medium" basic type="button" className="cancel-btn" disabled={isChangePasswordPending} onClick={onClose}>
            {t("v8_cancel", "Cancel")}
            <Icon className="xmark" />
          </Button>
          <Button color="green" type="button" className="save-btn" disabled={isChangePasswordPending} loading={isChangePasswordPending} onClick={handleSubmit(onSubmit)}>
            {t("v8_save", "Save")}
            <Icon className="check" />
          </Button>
        </div>
      </Modal.Actions>
    </>
  );
};
